import { ShieldAlert } from 'lucide-react'
import type { BriefTopThreat } from '../../hooks/useCompetitiveData'
import { toSentence } from '../../lib/utils'
import UrgencyBadge from './UrgencyBadge'

interface ThreatCardProps {
  threat: BriefTopThreat
}

function pointDirectionClasses(direction: string): string {
  if (direction === 'up') return 'border-red-200 bg-red-50 text-red-700'
  if (direction === 'down') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  return 'border-slate-200 bg-slate-100 text-slate-700'
}

function buildNarrative(threat: BriefTopThreat): {
  whatHappened: string
  whatWeDid: string
  whatYouShouldDo: string
} {
  const whatHappened = toSentence(
    threat.headline || threat.detail,
    `${threat.competitor_name} showed unusual competitive movement this week.`
  )
  const whatWeDid =
    threat.data_points.length > 0
      ? `We reviewed ${threat.data_points.length} signal${threat.data_points.length === 1 ? '' : 's'} and refreshed this threat profile.`
      : 'We scanned this competitor and no automated action was required.'
  const whatYouShouldDo = toSentence(
    threat.top_action?.trim() || threat.recommended_actions[0],
    'Continue monitoring this competitor for additional movement.'
  )

  return { whatHappened, whatWeDid, whatYouShouldDo }
}

export default function ThreatCard({ threat }: ThreatCardProps) {
  const narrative = buildNarrative(threat)

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="min-w-0">
          <p className="competitive-panel-title truncate">{threat.competitor_name}</p>
          <p className="competitive-body-text competitive-line-clamp-2 mt-1">{narrative.whatHappened}</p>
        </div>
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#dc2626]" />
      </div>

      <div className="space-y-3 p-4">
        <UrgencyBadge urgency={threat.urgency} />

        <div className="space-y-3">
          <div>
            <p className="competitive-support-label">What Happened</p>
            <p className="competitive-body-text competitive-line-clamp-2 mt-1">{narrative.whatHappened}</p>
          </div>
          <div>
            <p className="competitive-support-label">What We Did</p>
            <p className="competitive-body-text competitive-line-clamp-2 mt-1">{narrative.whatWeDid}</p>
          </div>
          <div>
            <p className="competitive-support-label">What You Should Do</p>
            <p className="competitive-body-text-strong competitive-line-clamp-2 mt-1">{narrative.whatYouShouldDo}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {threat.data_points.map((point, index) => (
            <span
              key={`${threat.competitor_id}-${point.label}-${index}`}
              className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${pointDirectionClasses(point.direction)}`}
              title={point.delta ?? undefined}
            >
              {point.label}: {point.value}
              {point.delta ? ` (${point.delta})` : ''}
            </span>
          ))}
        </div>
      </div>
    </article>
  )
}
